/**
 * WebcamCapture (Sprint 6 B5).
 *
 * Behavior:
 *   - Mount → request getUserMedia({video: true}) + start stream.
 *   - Capture snapshot mỗi 30s qua hidden <canvas>.
 *   - Upload multipart POST /api/proctoring/sessions/<id>/snapshots/
 *   - Display nhỏ ở corner (PiP-like) để student biết camera đang ghi.
 *
 * Error handling:
 *   - Permission denied → log violation HIGH `webcam_denied` + show alert.
 *   - Device not found → cùng pattern.
 *   - Stream interrupt (unplug) → log violation HIGH + try reconnect mỗi 5s.
 *
 * Privacy: stream chỉ chạy trong scope mounted. Unmount → stop tracks (LED tắt).
 *
 * TT 83 C3-b: khi `feature_flags.face_detection` bật, đếm số khuôn mặt mỗi 2 giây ngay trên
 * trình duyệt (`useFaceDetection`) → vi phạm `no_face` / `multiple_face`.
 */

import { useEffect, useRef, useState } from "react";
import { Alert } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { useUploadSnapshot } from "@/api/proctoring";
import { useAuthStore } from "@/stores/auth";
import { useFaceDetection } from "./useFaceDetection";

const SNAPSHOT_INTERVAL_MS = 30_000;
const JPEG_QUALITY = 0.7;
const SNAPSHOT_W = 320;
const SNAPSHOT_H = 240;

interface Props {
  sessionId: number;
  onViolation: (event: {
    type: "webcam_denied" | "no_face" | "multiple_face";
    severity: "high" | "medium";
    detail?: Record<string, unknown>;
  }) => void;
  /** Required: nếu false thì component không request permission (per-quiz off). */
  required: boolean;
}

export function WebcamCapture({ sessionId, onViolation, required }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sequenceRef = useRef(0);
  const [status, setStatus] = useState<"pending" | "active" | "denied" | "error">(
    required ? "pending" : "active",
  );
  const uploadMut = useUploadSnapshot(sessionId);
  // TT 83 C3-b — đếm khuôn mặt trên trình duyệt (flag app-level).
  const faceDetection = useAuthStore((s) => !!s.user?.feature_flags?.face_detection);
  useFaceDetection(videoRef, required && faceDetection && status === "active", onViolation);

  // ─── Setup stream ────────────────────────────────────────────────────
  useEffect(() => {
    if (!required) return;
    let cancelled = false;

    const setup = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: SNAPSHOT_W, height: SNAPSHOT_H },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {
            /* autoplay blocked sometimes */
          });
        }
        setStatus("active");
      } catch (err) {
        const name = (err as Error)?.name ?? "Unknown";
        setStatus(name === "NotAllowedError" ? "denied" : "error");
        onViolation({
          type: "webcam_denied",
          severity: "high",
          detail: { error: name },
        });
      }
    };

    void setup();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [required, onViolation]);

  // ─── Snapshot interval ───────────────────────────────────────────────
  useEffect(() => {
    if (status !== "active" || !required) return;
    const id = setInterval(() => {
      void captureAndUpload();
    }, SNAPSHOT_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, required]);

  async function captureAndUpload() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return;
    if (video.videoWidth === 0) return; // chưa ready

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY),
    );
    if (!blob) return;

    sequenceRef.current += 1;
    try {
      await uploadMut.mutateAsync({
        blob,
        type: "webcam",
        sequenceNo: sequenceRef.current,
        capturedAt: new Date().toISOString(),
      });
    } catch {
      // Best-effort — silent fail (network glitch). Sprint 7+ retry queue.
    }
  }

  if (!required) return null;

  return (
    <div
      data-testid="webcam-capture"
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1000,
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
        background: "#000",
      }}
    >
      {status === "denied" && (
        <Alert
          type="error"
          showIcon
          message="Webcam bị từ chối"
          description="Bài thi yêu cầu camera. Cho phép quyền trong browser settings rồi reload."
          style={{ maxWidth: 320 }}
        />
      )}
      {status === "error" && (
        <Alert
          type="warning"
          showIcon
          message="Không truy cập được camera"
          style={{ maxWidth: 320 }}
        />
      )}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          display: status === "active" ? "block" : "none",
          width: 160,
          height: 120,
          objectFit: "cover",
        }}
      />
      {status === "active" && (
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 4,
            color: "#fff",
            fontSize: 10,
            background: "rgba(0,0,0,0.5)",
            padding: "2px 4px",
            borderRadius: 4,
          }}
        >
          <CameraOutlined /> REC
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
